import type { Notification } from '@proref/types';
import { getTokens } from './token-store';

// The live half of the bell: a Server-Sent Events client for GET /v1/notifications/stream.
//
// Deliberately fetch() + ReadableStream rather than the browser's EventSource. EventSource cannot
// set an Authorization header, and the only alternative it leaves — the JWT in a query string —
// would be written verbatim into Caddy's access log on every connection. The fetch form keeps the
// token in the header, and it hands us explicit control over reconnect/backoff, which a stream
// with a server-side lifetime cap needs anyway.
//
// This is an ACCELERANT, not the source of truth. Everything it delivers also arrives on the
// hook's ordinary poll, so every failure path here degrades to "notifications show up within five
// minutes" rather than to a broken bell — which is why nothing below surfaces an error to the user.

const baseURL = import.meta.env.VITE_API_BASE_URL;

// Reconnect backoff. Capped low enough that a laptop waking from sleep reconnects promptly, high
// enough that an API restart doesn't get hammered by every open tab at once.
const BASE_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

// One decoded SSE frame. `event` defaults to 'message' per the spec; the server only sends
// 'notification' and 'reconnect'.
export interface SseEvent {
  event: string;
  data: string;
}

// Incremental SSE frame parser.
//
// A chunk boundary can land anywhere — mid-frame, mid-line, even mid-UTF-8-sequence — so this
// buffers whatever doesn't yet end a frame and returns only the complete ones. Exported for its
// own tests: this is the part where a naive `split('\n\n')` per chunk silently drops notifications
// under exactly the conditions (slow networks, large payloads) that are hardest to reproduce.
export function createSseParser(): { push(chunk: string): SseEvent[] } {
  let buffer = '';

  return {
    push(chunk: string): SseEvent[] {
      // Normalise line endings first: the spec allows CRLF, and a stray \r left on the end of a
      // data line ends up inside the JSON we hand to JSON.parse.
      buffer += chunk.replace(/\r\n?/g, '\n');
      const events: SseEvent[] = [];

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');

        let event = 'message';
        const dataLines: string[] = [];

        for (const line of raw.split('\n')) {
          // A line starting with ':' is a comment — that is what our heartbeat and the initial
          // ': connected' flush are. Keeping the connection alive is their entire job.
          if (line.startsWith(':') || line === '') continue;
          const colon = line.indexOf(':');
          const field = colon === -1 ? line : line.slice(0, colon);
          // One optional space after the colon is part of the framing, not of the value.
          const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
          if (field === 'event') event = value;
          else if (field === 'data') dataLines.push(value);
        }

        // A frame with no data field is framing-only (a lone comment block); nothing to deliver.
        if (dataLines.length > 0) events.push({ event, data: dataLines.join('\n') });
      }

      return events;
    },
  };
}

// Exponential backoff with full jitter. The jitter is what stops every tab a user has open from
// reconnecting in lockstep after the API restarts.
export function retryDelayMs(attempt: number): number {
  const capped = Math.min(MAX_RETRY_MS, BASE_RETRY_MS * 2 ** Math.max(0, attempt - 1));
  return Math.round(capped * (0.5 + Math.random() * 0.5));
}

export interface NotificationStreamHandlers {
  // A notification arrived. Called once per delivered row.
  onNotification(notification: Notification): void;
  // Connection came up / went down. Purely informational — the caller keeps polling either way.
  onConnectedChange?(connected: boolean): void;
}

// Open the stream and keep it open, reconnecting until the returned disposer is called.
// Returns immediately; the connection is established in the background.
export function connectNotificationStream(handlers: NotificationStreamHandlers): () => void {
  // Guard the environments that have no streaming fetch — jsdom under Vitest, and any browser old
  // enough to lack ReadableStream. The poll is the whole experience there, which is fine.
  if (typeof fetch !== 'function' || typeof AbortController !== 'function') {
    return () => {};
  }

  let stopped = false;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  async function readStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = createSseParser();

    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      // stream: true so a multi-byte character split across chunks is held, not mangled.
      for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
        if (frame.event === 'reconnect') {
          // A planned close at the server's lifetime cap, not a failure. Returning ends this
          // connection; the loop below reconnects with a fresh token and no backoff.
          return;
        }
        if (frame.event !== 'notification') continue;
        try {
          handlers.onNotification(JSON.parse(frame.data) as Notification);
        } catch {
          // A frame we can't parse is one notification the poll will pick up anyway. Dropping it
          // beats tearing down a working connection over one malformed payload.
        }
      }
    }
  }

  async function loop(): Promise<void> {
    let attempt = 0;

    while (!stopped) {
      const token = getTokens()?.access_token;

      if (token) {
        controller = new AbortController();
        try {
          const res = await fetch(`${baseURL}/notifications/stream`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
            signal: controller.signal,
          });

          if (res.ok && res.body) {
            // Only reset the backoff once a connection actually succeeded — resetting on attempt
            // alone would turn a persistent 500 into a 1-second retry loop forever.
            attempt = 0;
            handlers.onConnectedChange?.(true);
            await readStream(res.body);
          } else {
            // Includes a 401 from an expired access token. No refresh is attempted here on
            // purpose: the hook's poll runs through the axios client, whose interceptor already
            // owns single-flight refresh, so by the next retry getTokens() returns the new token.
            attempt += 1;
          }
        } catch {
          // Network drop, sleep/wake, or our own abort. Indistinguishable and handled the same.
          attempt += 1;
        } finally {
          handlers.onConnectedChange?.(false);
          controller = null;
        }
      } else {
        // Signed out mid-stream. Back off rather than spinning; the effect is torn down on logout
        // anyway, so this is only ever a brief window.
        attempt += 1;
      }

      if (stopped) return;
      await new Promise<void>((resolve) => {
        retryTimer = setTimeout(resolve, attempt === 0 ? 0 : retryDelayMs(attempt));
      });
    }
  }

  void loop();

  return () => {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    controller?.abort();
  };
}
